import { Dispatch } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { Response } from '../../models/shared';
import { EventSeatDTO } from '../../models/event-seat';
import { EventSector } from '../../models/ticket';
import baseQueryWithReauth from './base';
// eslint-disable-next-line import/no-cycle
import { eventsApi } from './events';
import { eventSectorsApi } from './event-sectors';

export interface ImportEventSeatsResultDTO {
  sectors: EventSector[];
  seats: EventSeatDTO[];
}

/**
 * Writes used by the event map. They hit the same endpoints as the event-seat
 * mutations in events.ts, but on purpose do NOT invalidate tags: painting 300
 * places would otherwise refetch the whole map 300 times. The caller runs a
 * batch and then invalidates once (see invalidateEventMapData in the event map).
 */
const eventMapApi = createApi({
  reducerPath: 'eventMapApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['event_map'],
  endpoints: (build) => ({
    assignVenueSeatsToSector: build.mutation<EventSeatDTO[], { eventId: string; sectorId: string; venueSeatIds: string[] }>({
      // One summary toast per batch instead of one per failed request.
      extraOptions: { silent: true },
      query: ({ eventId, sectorId, venueSeatIds }) => ({
        url: `/events/${eventId}/sectors/${sectorId}/seats/multiple-from-ids`,
        method: 'POST',
        body: { venue_seat_ids: venueSeatIds },
      }),
      transformResponse: (response: Response<EventSeatDTO[]>): EventSeatDTO[] => response.data || [],
    }),
    moveEventSeat: build.mutation<EventSeatDTO | null, { eventId: string; fromSectorId: string; eventSeatId: string; toSectorId: string }>({
      // One summary toast per batch instead of one per failed request.
      extraOptions: { silent: true },
      query: ({
        eventId, fromSectorId, eventSeatId, toSectorId,
      }) => ({
        url: `/events/${eventId}/sectors/${fromSectorId}/seats/${eventSeatId}`,
        method: 'PUT',
        body: { event_sector_id: toSectorId },
      }),
      transformResponse: (response: Response<EventSeatDTO>): EventSeatDTO | null => response.data || null,
    }),
    setEventSeatStatus: build.mutation<EventSeatDTO | null, { eventId: string; sectorId: string; eventSeatId: string; status: string }>({
      // One summary toast per batch instead of one per failed request.
      extraOptions: { silent: true },
      query: ({
        eventId, sectorId, eventSeatId, status,
      }) => ({
        url: `/events/${eventId}/sectors/${sectorId}/seats/${eventSeatId}`,
        method: 'PUT',
        body: { status },
      }),
      transformResponse: (response: Response<EventSeatDTO>): EventSeatDTO | null => response.data || null,
    }),
    removeEventSeat: build.mutation<void, { eventId: string; sectorId: string; eventSeatId: string }>({
      // One summary toast per batch instead of one per failed request.
      extraOptions: { silent: true },
      query: ({ eventId, sectorId, eventSeatId }) => ({
        url: `/events/${eventId}/sectors/${sectorId}/seats/${eventSeatId}`,
        method: 'DELETE',
      }),
    }),
    // Copies sectors, assignments and blocks from another event of the same business.
    importEventSeats: build.mutation<ImportEventSeatsResultDTO | null, { eventId: string; originalEventId: string }>({
      query: ({ eventId, originalEventId }) => ({
        url: `/events/${eventId}/seats/actions/import`,
        method: 'POST',
        body: { original_event_id: originalEventId, reset_status: true },
      }),
      transformResponse: (response: Response<ImportEventSeatsResultDTO>): ImportEventSeatsResultDTO | null => response.data || null,
    }),
  }),
});

export const {
  useAssignVenueSeatsToSectorMutation,
  useMoveEventSeatMutation,
  useSetEventSeatStatusMutation,
  useRemoveEventSeatMutation,
  useImportEventSeatsMutation,
} = eventMapApi;
export { eventMapApi };

/** Refetch the event map once after a batch of writes made with the endpoints above. */
export const invalidateEventMapCaches = (dispatch: Dispatch) => {
  dispatch(eventsApi.util.invalidateTags(['event_seats_raw', 'event_seats']));
  dispatch(eventSectorsApi.util.invalidateTags(['event_sectors', 'event_seats_raw']));
};
